/**
 * DS-internal hook : le conteneur est-il au moins aussi large que `threshold` ?
 *
 * Une disposition qui dépend de la place (une ligne ou deux niveaux, le bouton à côté ou dessous)
 * se tranche sur la largeur DU CONTENEUR, lue par `onLayout`, jamais sur celle de la fenêtre : la
 * même ligne vit dans une colonne, une page et une feuille, qui n'ont pas la même largeur au même
 * moment.
 *
 * Le crochet garde le BOOLÉEN, pas la largeur : React ignore une mise à jour identique, donc la vue
 * ne se re-rend qu'au passage du seuil — ni à chaque pixel d'un redimensionnement, ni après le
 * premier layout d'une vue qui reste étroite. Il démarre à `false` : on démarre étroit, parce que
 * ce qui s'élargit au premier rendu ne se voit pas, et ce qui se replie se verrait.
 *
 * `threshold` est une constante de l'appelant : un seuil qui bougerait sans que la largeur bouge ne
 * serait relu qu'au layout suivant.
 */
import { useCallback, useState } from "react"
import { type LayoutChangeEvent } from "react-native"

export type WidthAtLeast = {
    /** À poser sur la vue mesurée. */
    onLayout: (event: LayoutChangeEvent) => void
    /** Vrai quand la dernière largeur mesurée atteint le seuil ; faux avant toute mesure. */
    atLeast: boolean
}

export function useWidthAtLeast(threshold: number): WidthAtLeast {
    const [atLeast, setAtLeast] = useState(false)
    const onLayout = useCallback(
        (event: LayoutChangeEvent) => setAtLeast(event.nativeEvent.layout.width >= threshold),
        [threshold]
    )
    return { onLayout, atLeast }
}
